// Deployment-target extension point: which installation artifacts `pack` emits, and how
// the docker-compose output is shaped.
//
// v1 implements `docker-compose`. `kubernetes` and `podman` are declared here so a
// customer repo can already express the intent and the schema does not change when they
// land (EW-138, EW-139) — until then the tool fails with an explicit
// "not yet supported" error rather than emitting a half-baked descriptor.
import { z } from 'zod';
import { EnvVarNameSchema } from './env-var-name.js';
export const DeploymentTargetSchema = z.enum(['docker-compose', 'kubernetes', 'podman']);
/**
 * `local` wires the Keycloak Entra mock and dev values — this is the environment a
 * distro is tried out in on our side. `customer` emits real Entra settings and secret
 * references only.
 */
export const ComposeProfileSchema = z.enum(['local', 'customer']);
/**
 * Supporting-server image overrides. Left out, the product's own pins are used; a
 * customer overrides them only when their platform team mandates a specific image.
 */
export const ComposeImageOverridesSchema = z.strictObject({
    pgvector: z.string().min(1).optional(),
    redis: z.string().min(1).optional(),
    /**
     * The `local` profile's Entra-mock Keycloak. Overriding it is how a distribution rehearses
     * against the customer's actual IdP version (e.g. `quay.io/keycloak/keycloak:20.0.5` for
     * Gránit Atlas) instead of the product's pin. No effect on the `customer` profile — that one
     * carries no mock.
     */
    keycloak: z.string().min(1).optional(),
});
/**
 * What the `local` profile's IdP mock stands in for. It names the generated compose service
 * (and with it the in-network hostname): `entra-mock` when the mock mimics Entra, `keycloak`
 * when the customer's real IdP is Keycloak — the mock then IS a Keycloak (e.g. Gránit Atlas),
 * and a service called "entra-mock" would mislead whoever reads that stack. The realm inside
 * is `echo-work` either way. No effect on the `customer` profile — that one carries no mock.
 */
export const ComposeIdpSchema = z.enum(['entra-mock', 'keycloak']);
/** The control plane's admin roles (EW-65), by the names its AUTH_ROLE_* defaults use. */
export const MockAdminRoleSchema = z.enum(['Admin', 'AiAdmin', 'Auditor']);
/**
 * A user of the `local` profile's IdP mock. The password is an env-var NAME — the realm file
 * carries `${NAME}` and Keycloak substitutes it at import — so two users can hold two different
 * passwords, and none is ever written into a file. `groups` are `idpGroupId` values of the
 * declared group mappings (a desktop user's segment membership); `adminRoles` are client roles of
 * the admin client (an operator of the admin UI and of `admin apply`).
 */
export const ComposeMockUserSchema = z.strictObject({
    username: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/, 'lowercase, digits, . _ -'),
    passwordEnvVar: EnvVarNameSchema,
    groups: z.array(z.string().min(1)).optional(),
    adminRoles: z.array(MockAdminRoleSchema).optional(),
});
/**
 * The `local` profile's mock, beyond its image: the users it is seeded with. Left out, the
 * generator derives a default set — one test user per declared group mapping plus `admin`, `ai-admin`
 * and `auditor`, one admin role each, all on `KEYCLOAK_TEST_USER_PASSWORD`. No effect on the
 * `customer` profile, which carries no mock (`validate` refuses it there).
 */
export const ComposeMockSchema = z.strictObject({
    users: z.array(ComposeMockUserSchema).min(1),
});
export const ComposeOptionsSchema = z.strictObject({
    profile: ComposeProfileSchema,
    idp: ComposeIdpSchema.default('entra-mock'),
    images: ComposeImageOverridesSchema.optional(),
    mock: ComposeMockSchema.optional(),
    /**
     * Opt-in OpenTelemetry collector service. Not implemented yet: the generated compose
     * carries no collector service and the reference collector config is not copied into the
     * bundle either (both EW-24), so `validate` refuses `true` until that lands. The switch
     * itself stays, so a customer config written for EW-24 parses today.
     */
    observabilityCollector: z.boolean().default(false),
});
export const DeploymentSchema = z
    .strictObject({
    targets: z.array(DeploymentTargetSchema).min(1),
    compose: ComposeOptionsSchema.optional(),
})
    .refine((value) => !value.targets.includes('docker-compose') || value.compose !== undefined, {
    message: 'compose options are required when the docker-compose target is selected',
    path: ['compose'],
});
//# sourceMappingURL=deployment.js.map